import React, { useRef, useEffect, useState } from "react";
import Hls from "hls.js";
import { Spinner } from "reactstrap";

const looksLikeHls = (url?: string) => {
  if (!url) return false;
  const path = url.split("?")[0].toLowerCase();
  return path.endsWith(".m3u8");
};

const HlsPlayer: React.FC<{ src: string }> = ({
  src,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsInstanceRef = useRef<Hls | null>(null);
  const normalContainerRef = useRef<HTMLDivElement | null>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isCustomFullscreen, setIsCustomFullscreen] = useState(false);

  const toggleCustomFullscreen = () => {
    setIsCustomFullscreen((prev) => !prev);
  };

  useEffect(() => {
    const video = videoRef.current;
    const normalContainer = normalContainerRef.current;

    if (video && normalContainer && !video.parentElement) {
      normalContainer.appendChild(video);
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const normalContainer = normalContainerRef.current;
    const fullscreenContainer = fullscreenContainerRef.current;

    if (!video) return;

    if (isCustomFullscreen && fullscreenContainer) {
      fullscreenContainer.appendChild(video);
    } else if (!isCustomFullscreen && normalContainer) {
      normalContainer.appendChild(video);
    }
  }, [isCustomFullscreen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isCustomFullscreen) {
        setIsCustomFullscreen(false);
      }
    };

    if (isCustomFullscreen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isCustomFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      ) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          (document as any).msExitFullscreen();
        }
        toggleCustomFullscreen();
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!src) {
      setIsVideoLoading(false);
      return;
    }

    setIsVideoLoading(true);

    const handleCanPlay = () => setIsVideoLoading(false);
    const handleError = () => setIsVideoLoading(false);

    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("loadedmetadata", handleCanPlay);
    video.addEventListener("error", handleError);

    const isHlsUrl = looksLikeHls(src);
    const canPlayHlsNatively = video.canPlayType("application/vnd.apple.mpegurl");

    if (isHlsUrl) {
      if (canPlayHlsNatively) {
        video.src = src;
        return () => {
          video.removeEventListener("canplay", handleCanPlay);
          video.removeEventListener("loadedmetadata", handleCanPlay);
          video.removeEventListener("error", handleError);
          try {
            video.src = "";
          } catch (e) {}
        };
      }

      let hls: Hls | null = null;
      if (Hls.isSupported()) {
        hls = new Hls();
        hlsInstanceRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(video);

        // const onManifestParsed = () => {
        //   setIsVideoLoading(false);
        // };
        // hls.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);

        const onFragLoaded = (_: any, data: any) => {
          if (data.frag?.relurl?.includes("segment1.ts")) {
            setIsVideoLoading(false);
          }
        };
        hls.on(Hls.Events.FRAG_LOADED, onFragLoaded);

        const onHlsError = (_: any, data: any) => {
          console.error("hls.js error", data);
          if (data && data.fatal) {
            setIsVideoLoading(false);
          }
        };
        hls.on(Hls.Events.ERROR, onHlsError);

        return () => {
          video.removeEventListener("canplay", handleCanPlay);
          video.removeEventListener("loadedmetadata", handleCanPlay);
          video.removeEventListener("error", handleError);

          if (hls) {
            // hls.off(Hls.Events.MANIFEST_PARSED, onManifestParsed);
            hls.off(Hls.Events.FRAG_LOADED, onFragLoaded);
            hls.off(Hls.Events.ERROR, onHlsError);
            try {
              hls.destroy();
              hlsInstanceRef.current = null;
            } catch (e) {
            }
          } else {
            try {
              video.src = "";
            } catch (e) {}
          }
        };
      } else {
        video.src = src;
      }
    } else {
      video.src = src;
    }

    return () => {
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("loadedmetadata", handleCanPlay);
      video.removeEventListener("error", handleError);
      try {
        video.src = "";
      } catch (e) {}
    };
  }, [src]);

  return (
    <>
      <style>{`
        video::-webkit-media-controls-fullscreen-button {
          display: none !important;
        }
        video::-moz-media-controls-fullscreen-button {
          display: none !important;
        }
        video::-ms-media-controls-fullscreen-button {
          display: none !important;
        }
      `}</style>

      {isVideoLoading ? (
        <div className="text-center enhance-msg">
          <Spinner />
          <p>Loading video...</p>
        </div>
      ) : null}

      {/* Hidden video element that will be moved between containers */}
      <video
        ref={videoRef}
        controls
        controlsList="nodownload nofullscreen"
        disablePictureInPicture
        className={isCustomFullscreen ? "fullscreen-video" : "normal-video"}
        style={{
          width: isCustomFullscreen ? "100%" : "auto",
          height: isCustomFullscreen ? "100%" : "90vh",
          maxWidth: "100%",
          maxHeight: isCustomFullscreen ? "100%" : "90vh",
          borderRadius: isCustomFullscreen ? "0" : "10px",
          objectFit: isCustomFullscreen ? "contain" : "initial",
          display: isVideoLoading ? "none" : "block",
        }}
        crossOrigin="anonymous"
        onDoubleClick={(e) => {
          e.preventDefault();
          toggleCustomFullscreen();
        }}
      />

      {/* Custom fullscreen overlay */}
      {isCustomFullscreen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "#000",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Exit fullscreen button */}
          <button
            onClick={toggleCustomFullscreen}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "rgba(0, 0, 0, 0.7)",
              border: "none",
              color: "white",
              padding: "10px 15px",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "14px",
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
            title="Exit fullscreen (ESC)"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="currentColor"
            >
              <path d="M5.5 0a.5.5 0 0 1 .5.5v4A1.5 1.5 0 0 1 4.5 6h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5zm5 0a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 10 4.5v-4a.5.5 0 0 1 .5-.5zM0 10.5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 6 11.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zm10 1a1.5 1.5 0 0 1 1.5-1.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4z" />
            </svg>
            Exit Fullscreen
          </button>

          {/* Container for video in fullscreen */}
          <div
            ref={fullscreenContainerRef}
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        </div>
      )}

      {!isCustomFullscreen && (
        <div style={{ position: "relative" }}>
          <div ref={normalContainerRef} />
          
          {!isVideoLoading && (
            <button
              onClick={toggleCustomFullscreen}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "rgba(0, 0, 0, 0.7)",
                border: "none",
                color: "white",
                padding: "10px 15px",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "14px",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                gap: "5px",
                transition: "background 0.2s ease",
              }}
              title="Enter fullscreen (or double-click video)"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(0, 0, 0, 0.9)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(0, 0, 0, 0.7)";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z" />
              </svg>
              Fullscreen
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default HlsPlayer;